import EmptyState from '@/components/EmptyState';
import { FileSearchIcon } from '@/icons';
import { formatDateRange } from '@/utils';
import { Avatar, Flex, Typography } from 'antd';
import dayjs from 'dayjs';
import { motion } from 'framer-motion';
import { useGetTop10DisciplineIssuesQuery } from '../../../../../../app/features/employee/management-dashboard/management-dashboard.api';
import type { DisciplineIssueUser } from '../../../../../../app/features/employee/management-dashboard/management-dashboard.type';
import CustomRangePicker from '../../../../../../components/CustomRangePicker';
import useError from '../../../../../../hooks/useError';
import type { ManagementFilters } from '../../../../../../hooks/useManagementFilters';

const { Text } = Typography;

const Top10DisciplineIssues = ({
  department_id,
  office_id,
  filter: allFilters,
  setFilter,
  isFilterChanged,
  resetFilter,
}: {
  department_id?: string;
  office_id?: string;
  filter: ManagementFilters;
  setFilter: (key: any, value: any) => void;
  isFilterChanged: (key: any) => boolean;
  resetFilter: (key: any) => void;
}) => {
  const filter = allFilters.disciplineIssue;

  const last_days =
    dayjs(filter.end_date).diff(dayjs(filter.start_date), 'day') + 1;

  const { data: disciplineData, error } = useGetTop10DisciplineIssuesQuery({
    last_days,
    limit: 10,
    department_id,
    office_id,
  });
  useError(error, 'Failed to fetch top discipline issues');

  const issuesList = disciplineData?.data?.data || [];

  return (
    <div className="bg-theme-slate-900 border border-theme-gray-700 p-4 rounded-xl w-full space-y-4">
      {/* Header */}
      <Flex
        className="mb-4 text-white p-2"
        justify="space-between"
        align="center"
      >
        <h4 className="text-lg px-3 py-1 rounded border border-theme-gray-700 text-white leading-tight">
          Top 10 Discipline Issues
          <span className="text-theme-primary block text-sm">
            {formatDateRange(filter.start_date, filter.end_date)}
          </span>
        </h4>

        <CustomRangePicker
          startDate={filter.start_date}
          endDate={filter.end_date}
          onChange={(start, end) => {
            setFilter('disciplineIssue', {
              start_date: start,
              end_date: end,
            });
          }}
          disabledDate={(current) =>
            current > dayjs().subtract(1, 'day').endOf('day')
          }
          disabled={[false, true]}
          isChanged={isFilterChanged('disciplineIssue')}
          onReset={() => resetFilter('disciplineIssue')}
        />
      </Flex>

      {/* List */}
      <div className="mt-6 max-h-[330px] overflow-y-auto pr-2 custom-scrollbar">
        {issuesList.length === 0 ? (
          <div className="flex items-center justify-center h-[250px]">
            <EmptyState
              icon={<FileSearchIcon size={40} />}
              title="No Discipline Issues"
              description="No discipline issues tracked for the selected criteria."
              className="py-0"
            />
          </div>
        ) : (
          issuesList.map((item: DisciplineIssueUser, index: number) => {
            const value = parseFloat(item.wds_percentage_reverse);

            return (
              <Flex key={item.user_id} align="center" gap={16} className="mb-6">
                {/* Avatar */}
                <div className="flex flex-col items-center w-[80px] text-center">
                  <Avatar src={item.photo} size={42} className="shrink-0">
                    {item.name?.charAt(0)}
                  </Avatar>
                  <h6 className="text-[12px] font-medium mt-0.5 text-white text-center w-full truncate">
                    {item.name}
                  </h6>
                  <p className="text-[11px] -mb-1">
                    ID: {item.employee_id || '--'}
                  </p>
                  <p className="text-[11px]">
                    Department: {item?.department_name || '--'}
                  </p>
                </div>

                {/* Progress */}

                <div className="flex-1 max-w-[60%]">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${value}%` }}
                    transition={{ duration: 0.8, delay: index * 0.1 }}
                    className="relative h-11 rounded-r-lg bg-[#C96808]"
                  >
                    <Text className="!text-white text-2xl font-semibold block leading-tight text-nowrap absolute left-full mt-1.5 ml-2">
                      {value}%
                    </Text>
                  </motion.div>
                </div>
              </Flex>
            );
          })
        )}
      </div>
    </div>
  );
};

export default Top10DisciplineIssues;
